import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Layout from "../components/Layout";
import AIInsightCard from "../components/AIInsightCard";
import SkillBar from "../components/SkillBar";
import SkillRadar from "../components/SkillRadar";
import LearningPathPanel from "../components/LearningPathPanel";
import CareerTreeView from "../components/CareerTreeView";
import CareerRadar from "../components/CareerRadar";
import CareerStepTimeline from "../components/CareerStepTimeline";
import TargetRoleBanner from "../components/TargetRoleBanner";
import ReadinessBoard from "../components/ReadinessBoard";
import KudosPointsCard from "../components/KudosPointsCard";
import RewardsCatalog from "../components/RewardsCatalog";
import YggHistoryTimeline from "../components/YggHistoryTimeline";
import YggSurveyForm from "../components/YggSurveyForm";
import { MentorProfileEditor, MentorDiscovery, MyMentoringEngagements } from "../components/MentorProgramComponents";
import { listYggCycles } from "../lib/api";
import { JobBoard } from "../components/JobBoardModule";
import RecognitionModule from "../components/RecognitionModule";import MentorDetailDrawer from "../components/MentorDetailDrawer";
import MentorInboxPanel from "../components/MentorInboxPanel";
import MenteeOutboxPanel from "../components/MenteeOutboxPanel";
import DevelopmentSuggestionsPanel from "../components/DevelopmentSuggestionsPanel";
import IDPEditor from "../components/IDPEditor";
import NextActionsCard from "../components/NextActionsCard";
import { ReadinessExplainerDrawer } from "../components/ExplainerDrawers";
import {
  fetchEmployee,
  fetchSkillGap,
  fetchSkillGapInsight,
  fetchCareerPaths,
  fetchCareerPathsInsight,
  fetchMentorMatches,
  fetchMentorMatchesInsight,
  fetchRoles,
  fetchDevelopmentPlan,
  fetchMentorRequests,
  fetchTargetRole,
} from "../lib/api";
import { generateDevelopmentPlanPdf } from "../lib/pdfExport";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { TrendingUp, Target, ArrowRight, GraduationCap, Clock, CheckCircle2, AlertCircle, Briefcase, Compass, Download, Sparkles, BookOpen, Inbox, Send, User, ShieldCheck } from "lucide-react";

export default function EmployeeDashboard() {
  const persona = JSON.parse(sessionStorage.getItem("plena_persona") || "null");
  const { tab } = useParams();
  const nav = useNavigate();
  const loc = useLocation();
  const empId = persona?.employee_id;

  const [employee, setEmployee] = useState(null);
  const [roles, setRoles] = useState([]);
  const [targetRoleId, setTargetRoleId] = useState(null);
  const [gap, setGap] = useState(null);
  const [gapInsight, setGapInsight] = useState(null);
  const [paths, setPaths] = useState(null);
  const [pathsInsight, setPathsInsight] = useState(null);
  const [mentors, setMentors] = useState(null);
  const [mentorsInsight, setMentorsInsight] = useState(null);
  const [loading, setLoading] = useState({ gap: true, paths: true, mentors: true, gapAi: true, pathsAi: true, mentorsAi: true });
  // Faz 3: target role state + rewards dialog
  const [pinnedTarget, setPinnedTarget] = useState(null);          // from /target-role
  const [targetRefreshKey, setTargetRefreshKey] = useState(0);
  const [rewardsOpen, setRewardsOpen] = useState(false);

  const activeTab = useMemo(() => {
    // Career group inner tabs (order matters — check most specific first)
    if (loc.pathname.includes("/career-tree")) return "career-tree";
    if (loc.pathname.includes("/job-board")) return "job-board";
    if (loc.pathname.includes("/recognitions")) return "recognitions";
    if (loc.pathname.includes("/ygg")) return "ygg";
    if (loc.pathname.includes("/mentor-profile") || loc.pathname.includes("/mentor")) return "mentor";
    if (loc.pathname.includes("/career")) return "career";
    if (loc.pathname.includes("/mentors")) return "mentors";
    if (loc.pathname.includes("/skills")) return "skills";
    if (loc.pathname.includes("/learning")) return "learning";
    if (loc.pathname.includes("/idp")) return "idp";
    if (loc.pathname.includes("/inbox")) return "inbox";
    return "overview";
  }, [loc.pathname]);

  // Group hierarchy: outer tabs group inner tabs
  const TAB_TO_GROUP = {
    overview: "profile", skills: "profile",
    career: "career", "career-tree": "career", "job-board": "career", recognitions: "career", ygg: "career", mentor: "career",
    learning: "growth", mentors: "growth", idp: "growth",
    inbox: "inbox",
  };
  const activeGroup = TAB_TO_GROUP[activeTab] || "profile";

  // Mentor inbox + Mentee outbox badge counts
  const [inboxPending, setInboxPending] = useState(0);
  const [outboxPending, setOutboxPending] = useState(0);
  const [activityInnerTab, setActivityInnerTab] = useState(() => {
    // Allow deep-link via hash: #suggestions opens that tab directly
    if (typeof window !== "undefined" && window.location.hash === "#suggestions") return "suggestions";
    return "inbox";
  });

  // Mentor drawer state
  const [mentorDrawerId, setMentorDrawerId] = useState(null);
  const [mentorDrawerOpen, setMentorDrawerOpen] = useState(false);

  // Explainer drawers
  const [showReadinessExplainer, setShowReadinessExplainer] = useState(false);

  // PDF state
  const [pdfLoading, setPdfLoading] = useState(false);

  const openMentorDetail = (mentorId) => {
    if (!empId || !mentorId) return;
    // Use composite mentee__mentor format so backend can compute fresh detail
    setMentorDrawerId(`${empId}__${mentorId}`);
    setMentorDrawerOpen(true);
  };

  const handleDownloadPdf = async () => {
    if (!empId) return;
    setPdfLoading(true);
    try {
      const plan = await fetchDevelopmentPlan(empId, targetRoleId);
      await generateDevelopmentPlanPdf(plan);
      toast.success("Gelişim planın indirildi", { description: "PDF dosyasını e-postanla yöneticinle paylaşabilirsin." });
    } catch (e) {
      toast.error("PDF oluşturulamadı", { description: "Lütfen tekrar deneyin." });
    } finally {
      setPdfLoading(false);
    }
  };

  useEffect(() => {
    if (!persona) {
      nav("/");
      return;
    }
    fetchEmployee(empId).then((emp) => {
      setEmployee(emp);
      // Hedef rol career-paths effect tarafından top promotion'a ayarlanır;
      // burada sabit current-role'a kilitleme YAPMA — aksi halde haftalık öğrenme planı
      // mevcut rolde kalmayı önerir.
    });
    fetchRoles().then(setRoles);
    // Mentor inbox pending count (badge on tab)
    fetchMentorRequests({ mentor_id: empId, status: "pending" })
      .then((r) => setInboxPending(r.items?.length || 0))
      .catch(() => setInboxPending(0));
    // Mentee outbox pending count
    fetchMentorRequests({ mentee_id: empId, status: "pending" })
      .then((r) => setOutboxPending(r.items?.length || 0))
      .catch(() => setOutboxPending(0));
    // Faz 3: pinned target rol
    fetchTargetRole(empId).then((t) => setPinnedTarget(t)).catch(() => setPinnedTarget(null));
  }, [empId, targetRefreshKey]);

  useEffect(() => {
    if (!empId) return;
    setLoading((l) => ({ ...l, gap: true, gapAi: true }));
    setGapInsight(null);
    fetchSkillGap(empId, targetRoleId)
      .then(setGap)
      .catch(() => setGap({ readiness_score: null, gaps: [], aligned: [], exceeds: [] }))
      .finally(() => setLoading((l) => ({ ...l, gap: false })));
    fetchSkillGapInsight(empId, targetRoleId)
      .then(setGapInsight)
      .catch(() => setGapInsight(null))
      .finally(() => setLoading((l) => ({ ...l, gapAi: false })));
  }, [empId, targetRoleId]);

  useEffect(() => {
    if (!empId) return;
    setLoading((l) => ({ ...l, paths: true, mentors: true, pathsAi: true, mentorsAi: true }));
    fetchCareerPaths(empId)
      .then((p) => {
        setPaths(p);
        // Auto-pick top promotion path as default learning target — daha anlamlı haftalık plan üretir
        const topPromo = (p?.paths || []).find((x) => x.is_promotion) || (p?.paths || [])[0];
        if (topPromo?.role_id) {
          setTargetRoleId((cur) => cur || topPromo.role_id);
        }
      })
      .catch(() => setPaths({ paths: [] }))
      .finally(() => setLoading((l) => ({ ...l, paths: false })));
    fetchMentorMatches(empId)
      .then(setMentors)
      .catch(() => setMentors({ matches: [] }))
      .finally(() => setLoading((l) => ({ ...l, mentors: false })));
    fetchCareerPathsInsight(empId)
      .then(setPathsInsight)
      .catch(() => setPathsInsight(null))
      .finally(() => setLoading((l) => ({ ...l, pathsAi: false })));
    fetchMentorMatchesInsight(empId)
      .then(setMentorsInsight)
      .catch(() => setMentorsInsight(null))
      .finally(() => setLoading((l) => ({ ...l, mentorsAi: false })));
  }, [empId]);

  const retryGap = async () => {
    setLoading((l) => ({ ...l, gapAi: true }));
    try {
      const ai = await fetchSkillGapInsight(empId, targetRoleId);
      setGapInsight(ai);
    } catch {
      setGapInsight(null);
    } finally {
      setLoading((l) => ({ ...l, gapAi: false }));
    }
  };
  const retryPaths = async () => {
    setLoading((l) => ({ ...l, pathsAi: true }));
    try {
      const ai = await fetchCareerPathsInsight(empId);
      setPathsInsight(ai);
    } catch {
      setPathsInsight(null);
    } finally {
      setLoading((l) => ({ ...l, pathsAi: false }));
    }
  };
  const retryMentors = async () => {
    setLoading((l) => ({ ...l, mentorsAi: true }));
    try {
      const ai = await fetchMentorMatchesInsight(empId);
      setMentorsInsight(ai);
    } catch {
      setMentorsInsight(null);
    } finally {
      setLoading((l) => ({ ...l, mentorsAi: false }));
    }
  };

  if (!persona) return null;

  return (
    <Layout persona={persona}>
      {/* Header */}
      <div className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="label-eyebrow text-sky-600 mb-2">Çalışan Görünümü</div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">
            Merhaba, <span className="text-slate-700">{persona.name?.split(" ")[0]}</span>
          </h1>
          <p className="text-base text-slate-600 mt-3 max-w-2xl leading-relaxed">
            Yetkinlik profilin, kariyer yolun ve önerilen mentorlarına buradan tek bakışta erişebilirsin.
          </p>
          {pinnedTarget?.target_role && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-sm text-amber-900" data-testid="hero-target-pill">
              <Target className="w-3.5 h-3.5 text-amber-600" />
              <span>Hedefin: <span className="font-semibold">{pinnedTarget.target_role.title}</span></span>
              <button onClick={() => nav("/employee/career")} className="text-xs text-amber-700 hover:underline ml-1" data-testid="hero-target-view-btn">Detay →</button>
            </div>
          )}
        </div>
        {employee && (
          <div className="flex items-center gap-3">
            <Button
              data-testid="download-plan-pdf-btn"
              onClick={handleDownloadPdf}
              disabled={pdfLoading}
              variant="outline"
              className="border-sky-300 text-sky-800 hover:bg-sky-50"
            >
              <Download className="w-4 h-4 mr-2" />
              {pdfLoading ? "Hazırlanıyor…" : "Gelişim Planı PDF"}
            </Button>
            <div className="flex items-center gap-4 card-surface px-5 py-3">
              <Avatar className="w-12 h-12 ring-2 ring-sky-500/30">
                <AvatarImage src={employee.avatar} />
                <AvatarFallback>{employee.name?.charAt(0)}</AvatarFallback>
              </Avatar>
              <div>
                <div className="text-sm font-medium">{employee.title}</div>
                <div className="text-xs text-slate-500">{employee.department} · {employee.tenure_years} yıl</div>
              </div>
            </div>
          </div>
        )}
      </div>

      <Tabs
        value={activeGroup}
        onValueChange={(g) => {
          // When switching group, jump to its first inner tab
          const firstTab = { profile: "overview", career: "career", growth: "learning", inbox: "inbox" }[g];
          const map = { overview: "/employee", skills: "/employee/skills", career: "/employee/career", "career-tree": "/employee/career-tree", "job-board": "/employee/job-board", recognitions: "/employee/recognitions", learning: "/employee/learning", mentors: "/employee/mentors", inbox: "/employee/inbox" };
          nav(map[firstTab]);
        }}
      >
        <TabsList className="bg-white border border-slate-200 mb-6 p-1.5 h-auto flex-wrap">
          <TabsTrigger value="profile" data-testid="grp-tab-profile" className="text-base px-5 py-2.5 data-[state=active]:bg-sky-50 data-[state=active]:text-sky-800 data-[state=active]:shadow-sm">
            <User className="w-4 h-4 mr-1.5" /> Profilim
          </TabsTrigger>
          <TabsTrigger value="career" data-testid="grp-tab-career" className="text-base px-5 py-2.5 data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-800 data-[state=active]:shadow-sm">
            <Compass className="w-4 h-4 mr-1.5" /> Kariyerim
          </TabsTrigger>
          <TabsTrigger value="growth" data-testid="grp-tab-growth" className="text-base px-5 py-2.5 data-[state=active]:bg-sky-50 data-[state=active]:text-sky-800 data-[state=active]:shadow-sm">
            <BookOpen className="w-4 h-4 mr-1.5" /> Gelişimim
          </TabsTrigger>
          <TabsTrigger value="inbox" data-testid="grp-tab-inbox" className="text-base px-5 py-2.5 data-[state=active]:bg-sky-50 data-[state=active]:text-sky-800 data-[state=active]:shadow-sm relative">
            <Inbox className="w-4 h-4 mr-1.5" /> Gelen Kutum
            {inboxPending + outboxPending > 0 && (
              <span
                data-testid="inbox-badge"
                className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-[0.65rem] font-bold bg-rose-600 text-white"
              >
                {inboxPending + outboxPending}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ============== GROUP: PROFILIM ============== */}
        <TabsContent value="profile">
          <Tabs
            value={activeTab}
            onValueChange={(v) => {
              const map = { overview: "/employee", skills: "/employee/skills" };
              nav(map[v]);
            }}
          >
            <TabsList className="bg-slate-100 border border-slate-200 mb-6 p-1 h-auto">
              <TabsTrigger value="overview" data-testid="tab-overview" className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-sky-800 data-[state=active]:shadow-sm">Genel Bakış</TabsTrigger>
              <TabsTrigger value="skills" data-testid="tab-skills" className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-sky-800 data-[state=active]:shadow-sm">Yetkinlik Profili</TabsTrigger>
            </TabsList>

        {/* Overview */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <button
              type="button"
              onClick={() => setShowReadinessExplainer(true)}
              data-testid="readiness-kpi-btn"
              className="text-left hover:ring-2 hover:ring-sky-300 hover:-translate-y-0.5 transition rounded-xl"
              title="Hazır olma skoru detayını gör"
            >
              <StatCard label="Hazır Olma Skoru" value={`%${gap?.readiness_score ?? "-"}`} sub={`Hedef rol: ${gap?.target_role?.title || "-"} · Tıkla, detay aç`} icon={Target} accent="sky" />
            </button>
            <StatCard label="Performans Notu" value={employee?.performance_rating || "-"} sub="Son dönem" icon={TrendingUp} accent="amber" />
            <StatCard label="Bağlılık" value={`${employee?.engagement_score ?? "-"}/100`} sub="Pulse" icon={CheckCircle2} accent="emerald" />
          </div>

          <ReadinessExplainerDrawer
            open={showReadinessExplainer}
            onClose={() => setShowReadinessExplainer(false)}
            employee={employee}
            gap={gap}
          />

          <NextActionsCard
            persona={persona}
            role="employee"
            inboxTabPath="/employee/inbox"
          />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 card-surface p-6">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <div className="label-eyebrow mb-1">Hedef Role Hazır Olma</div>
                  <h3 className="font-display text-2xl font-semibold">
                    {gap?.target_role?.title || (loading.gap ? "Hazırlanıyor…" : "—")}
                  </h3>
                </div>
                {gap?.readiness_score != null && (
                  <Badge variant="outline" className="border-sky-500/40 text-sky-700 bg-sky-500/10">
                    %{gap.readiness_score}
                  </Badge>
                )}
              </div>
              {loading.gap && (!gap || !gap.gaps?.length) && (
                <div className="space-y-3 mb-5">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="space-y-1.5">
                      <div className="h-3 bg-slate-200/60 rounded animate-pulse w-1/3" />
                      <div className="h-2 bg-slate-100 rounded-full animate-pulse" />
                    </div>
                  ))}
                </div>
              )}
              <div className="space-y-3 mb-5">
                {gap?.gaps?.slice(0, 4).map((g) => (
                  <SkillBar key={g.skill_id} name={g.skill_name} current={g.current_level} target={g.target_level} />
                ))}
                {gap?.aligned?.slice(0, 2).map((g) => (
                  <SkillBar key={g.skill_id} name={g.skill_name} current={g.current_level} target={g.target_level} />
                ))}
              </div>
            </div>
            <AIInsightCard title="Skill Gap Yorumu" loading={loading.gapAi} insight={gapInsight} onRetry={retryGap} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="card-surface p-6">
              <div className="flex items-center justify-between mb-5">
                <div className="label-eyebrow">Önerilen Kariyer Yolları</div>
                <Button variant="ghost" size="sm" onClick={() => nav("/employee/career")} className="text-sky-600 hover:text-sky-700">
                  Tümü <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
              <div className="space-y-3">
                {paths?.paths?.slice(0, 3).map((p) => <PathRow key={p.role_id} path={p} />)}
              </div>
            </div>
            <div className="card-surface p-6">
              <div className="flex items-center justify-between mb-5">
                <div className="label-eyebrow">Mentor Önerileri</div>
                <Button variant="ghost" size="sm" onClick={() => nav("/employee/mentors")} className="text-sky-600 hover:text-sky-700">
                  Tümü <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
              <div className="space-y-3">
                {mentors?.matches?.slice(0, 2).map((m) => <MentorRowMini key={m.mentor_id} m={m} onOpenDetail={() => openMentorDetail(m.mentor_id)} />)}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Skills */}
        <TabsContent value="skills" className="space-y-6">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm text-slate-600">Hedef rol:</span>
            <Select value={targetRoleId || gap?.target_role?.id || ""} onValueChange={setTargetRoleId}>
              <SelectTrigger className="w-[280px] bg-white border-slate-200" data-testid="target-role-select">
                <SelectValue placeholder="Rol seçin" />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200">
                {roles.map((r) => (
                  <SelectItem key={r.id} value={r.id}>{r.title} — {r.career_band}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 space-y-5">
              {/* Skill radar against target role */}
              {employee?.id && <SkillRadar employeeId={employee.id} targetRoleId={targetRoleId || employee.role_id} height={320} />}

              {/* Always show current skill profile */}
              <div className="card-surface p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Compass className="w-4 h-4 text-sky-600" />
                  <div className="label-eyebrow">Mevcut Yetkinlik Profili</div>
                  <span className="ml-auto text-xs font-mono text-slate-500">{employee?.current_skills?.length || 0} beceri</span>
                </div>
                <div className="space-y-3">
                  {employee?.current_skills?.map((s) => (
                    <SkillBar key={s.skill_id} name={s.skill_name} current={s.level} evidence={s.evidence} showTarget={false} />
                  ))}
                </div>
              </div>

              {gap && (
                <>
                  <SkillSection title="Açıklar (Gap)" items={gap.gaps} icon={AlertCircle} accent="amber" emptyText="Açık beceri yok 🎉" />
                  <SkillSection title="Uyumlu" items={gap.aligned} icon={CheckCircle2} accent="emerald" emptyText="-" />
                  <SkillSection title="Hedefin Üzerinde" items={gap.exceeds} icon={TrendingUp} accent="sky" emptyText="-" />
                </>
              )}
            </div>
            <div className="space-y-5">
              <AIInsightCard title="Skill Gap Yorumu" loading={loading.gapAi} insight={gapInsight} onRetry={retryGap} />
              <div className="card-surface p-6">
                <div className="label-eyebrow mb-2">Mevcut Profil</div>
                <div className="text-3xl font-display font-semibold mb-1">{employee?.current_skills?.length || 0}</div>
                <div className="text-xs text-slate-500">Tanımlı beceri</div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Career (was inside profile group; now moved to its own top-level group below) */}
          </Tabs>
        </TabsContent>

        {/* ============== GROUP: KARİYERİM ============== */}
        <TabsContent value="career">
          {/* Persistent target-role banner — visible across all Kariyerim inner tabs */}
          <div className="mb-5" data-testid="career-target-banner-wrap">
            <TargetRoleBanner
              employeeId={empId}
              onChanged={() => setTargetRefreshKey((k) => k + 1)}
              onSelectTarget={() => nav("/employee/career-tree")}
            />
          </div>
          <Tabs
            value={["career","career-tree","job-board","recognitions","ygg","mentor"].includes(activeTab) ? activeTab : "career"}
            onValueChange={(v) => {
              const map = { career: "/employee/career", "career-tree": "/employee/career-tree", "job-board": "/employee/job-board", recognitions: "/employee/recognitions", ygg: "/employee/ygg", mentor: "/employee/mentor-profile" };
              nav(map[v]);
            }}
          >
            <TabsList className="bg-slate-100 border border-slate-200 mb-6 p-1 h-auto flex-wrap">
              <TabsTrigger value="career" data-testid="tab-career" className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-emerald-800 data-[state=active]:shadow-sm">
                <Briefcase className="w-3.5 h-3.5 mr-1.5" /> Kariyer Yolları
              </TabsTrigger>
              <TabsTrigger value="career-tree" data-testid="tab-career-tree" className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-emerald-800 data-[state=active]:shadow-sm">
                <Compass className="w-3.5 h-3.5 mr-1.5" /> Kariyer Ağacı
              </TabsTrigger>
              <TabsTrigger value="ygg" data-testid="tab-ygg" className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-emerald-800 data-[state=active]:shadow-sm">
                <ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> YGG Değerlendirmem
              </TabsTrigger>
              <TabsTrigger value="job-board" data-testid="tab-job-board" className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-emerald-800 data-[state=active]:shadow-sm">
                <Send className="w-3.5 h-3.5 mr-1.5" /> İç İlanlar · Niyet Mektubu
              </TabsTrigger>
              <TabsTrigger value="mentor" data-testid="tab-mentor" className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-emerald-800 data-[state=active]:shadow-sm">
                <User className="w-3.5 h-3.5 mr-1.5" /> Mentorluğum
              </TabsTrigger>
              <TabsTrigger value="recognitions" data-testid="tab-recognitions" className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-emerald-800 data-[state=active]:shadow-sm">
                <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Takdirlerim
              </TabsTrigger>
            </TabsList>

        {/* Career Yolları (existing path cards + AI insight + NEW timeline + radar + readiness) */}
        <TabsContent value="career" className="space-y-6">
          {/* Timeline: past → current → next → future */}
          <CareerStepTimeline
            employeeId={empId}
            onPickNext={async (step) => {
              try {
                const { setTargetRole: apiSetTarget } = await import("../lib/api");
                await apiSetTarget(empId, { target_role_id: step.role_id, note: "" });
                toast.success(`🎯 Hedefin belirlendi: ${step.role_title}`);
                setTargetRefreshKey((k) => k + 1);
              } catch { toast.error("Hedef güncellenemedi"); }
            }}
          />
          {/* Readiness board (only when a target is pinned) */}
          {pinnedTarget?.target_role_id && (
            <ReadinessBoard
              employeeId={empId}
              targetRoleId={pinnedTarget.target_role_id}
              onAddToIdp={({ criterion }) => {
                toast.success(`"${criterion.label}" IDP aksiyon olarak taslak eklendi (Gelişimim → IDP'de düzenleyebilirsin).`);
                nav("/employee/idp");
              }}
            />
          )}
          {/* Radar */}
          <CareerRadar employeeId={empId} targetRoleId={pinnedTarget?.target_role_id} />
          {/* Classic path cards for context */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 space-y-4">
              <div className="label-eyebrow">Diğer Aday Roller</div>
              {paths?.paths?.map((p, i) => <CareerPathCard key={p.role_id} path={p} index={i} />)}
            </div>
            <div className="space-y-5">
              <AIInsightCard title="Kariyer Önerisi" loading={loading.pathsAi} insight={pathsInsight} onRetry={retryPaths} />
              {employee?.career_aspirations && (
                <div className="card-surface p-6">
                  <div className="label-eyebrow mb-2">Belirttiğin hedef</div>
                  <p className="text-sm text-slate-800 italic">&quot;{employee.career_aspirations}&quot;</p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Kariyer Ağacı */}
        <TabsContent value="career-tree" className="space-y-6">
          <CareerTreeView employeeId={empId} onTargetChange={() => setTargetRefreshKey((k) => k + 1)} />
        </TabsContent>

        {/* İç İlanlar · Niyet Mektubu */}
        <TabsContent value="job-board" className="space-y-6">
          <JobBoard employeeId={empId} />
        </TabsContent>

        {/* Takdirler — with KudosPointsCard + Rewards CTA */}
        <TabsContent value="recognitions" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2">
              <RecognitionModule currentEmployeeId={empId} mode="employee" />
            </div>
            <div>
              <KudosPointsCard employeeId={empId} onOpenCatalog={() => setRewardsOpen(true)} />
            </div>
          </div>
        </TabsContent>

        {/* YGG — Yetenek Gözden Geçirme */}
        <TabsContent value="ygg" className="space-y-6" data-testid="tabcontent-ygg">
          <YggEmployeeSection empId={empId} />
        </TabsContent>

        {/* Mentor Program */}
        <TabsContent value="mentor" className="space-y-6" data-testid="tabcontent-mentor">
          <MyMentoringEngagements employeeId={empId} />
          <MentorProfileEditor employeeId={empId} />
          <MentorDiscovery employeeId={empId} />
        </TabsContent>
          </Tabs>
          {/* Rewards catalog modal */}
          <RewardsCatalog employeeId={empId} open={rewardsOpen} onOpenChange={setRewardsOpen} />
        </TabsContent>

        {/* ============== GROUP: GELİŞİMİM ============== */}
        <TabsContent value="growth">
          <Tabs
            value={activeTab === "mentors" ? "mentors" : activeTab === "idp" ? "idp" : "learning"}
            onValueChange={(v) => {
              const map = { learning: "/employee/learning", mentors: "/employee/mentors", idp: "/employee/idp" };
              nav(map[v]);
            }}
          >
            <TabsList className="bg-slate-100 border border-slate-200 mb-6 p-1 h-auto">
              <TabsTrigger value="learning" data-testid="tab-learning" className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-sky-800 data-[state=active]:shadow-sm">
                <BookOpen className="w-3.5 h-3.5 mr-1.5" /> Öğrenme Yolu
              </TabsTrigger>
              <TabsTrigger value="mentors" data-testid="tab-mentors" className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-sky-800 data-[state=active]:shadow-sm">
                Mentor Eşleşmeleri
              </TabsTrigger>
              <TabsTrigger value="idp" data-testid="tab-idp" className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-sky-800 data-[state=active]:shadow-sm">
                <Target className="w-3.5 h-3.5 mr-1.5" /> Bireysel Gelişim Planı
              </TabsTrigger>
            </TabsList>

        {/* Mentors */}
        <TabsContent value="mentors" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 space-y-4">
              {mentors?.matches?.map((m) => <MentorCard key={m.mentor_id} m={m} onOpenDetail={() => openMentorDetail(m.mentor_id)} />)}
            </div>
            <div className="space-y-5">
              <AIInsightCard title="Eşleşme Açıklaması" loading={loading.mentorsAi} insight={mentorsInsight} onRetry={retryMentors} />
            </div>
          </div>
        </TabsContent>

        {/* Learning path */}
        <TabsContent value="learning" className="space-y-6">
          <DevelopmentSuggestionsPanel employeeId={empId} />
          <LearningPathPanel employeeId={empId} targetRoleId={targetRoleId || employee?.role_id} />
        </TabsContent>

        {/* IDP */}
        <TabsContent value="idp" className="space-y-6">
          <IDPEditor employeeId={empId} currentUser="employee" />
        </TabsContent>
          </Tabs>
        </TabsContent>

        {/* ============== GROUP: GELEN KUTUM ============== */}
        <TabsContent value="inbox" className="space-y-6">
          <Tabs value={activityInnerTab} onValueChange={setActivityInnerTab}>
            <TabsList className="bg-slate-100 border border-slate-200 mb-5 p-1 h-auto">
              <TabsTrigger
                value="inbox"
                data-testid="inner-tab-inbox"
                className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-sky-800 data-[state=active]:shadow-sm relative"
              >
                <Inbox className="w-3.5 h-3.5 mr-1.5" /> Mentor Talepleri (Bana)
                {inboxPending > 0 && (
                  <span className="ml-2 inline-flex items-center justify-center min-w-[1.1rem] h-4 px-1.5 rounded-full text-[0.6rem] font-bold bg-sky-600 text-white">
                    {inboxPending}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger
                value="suggestions"
                data-testid="inner-tab-suggestions"
                className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-violet-800 data-[state=active]:shadow-sm relative"
              >
                <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Gelişim Önerileri
              </TabsTrigger>
              <TabsTrigger
                value="outbox"
                data-testid="inner-tab-outbox"
                className="text-sm px-4 py-1.5 data-[state=active]:bg-white data-[state=active]:text-emerald-800 data-[state=active]:shadow-sm relative"
              >
                <Send className="w-3.5 h-3.5 mr-1.5" /> Gönderdiklerim
                {outboxPending > 0 && (
                  <span className="ml-2 inline-flex items-center justify-center min-w-[1.1rem] h-4 px-1.5 rounded-full text-[0.6rem] font-bold bg-emerald-600 text-white">
                    {outboxPending}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="inbox" className="space-y-6">
              <MentorInboxPanel employeeId={empId} />
            </TabsContent>
            <TabsContent value="suggestions" className="space-y-6">
              <DevelopmentSuggestionsPanel employeeId={empId} mode="received" />
            </TabsContent>
            <TabsContent value="outbox" className="space-y-6">
              <MenteeOutboxPanel employeeId={empId} />
            </TabsContent>
          </Tabs>
        </TabsContent>
      </Tabs>

      <MentorDetailDrawer
        matchId={mentorDrawerId}
        open={mentorDrawerOpen}
        onOpenChange={setMentorDrawerOpen}
      />
    </Layout>
  );
}

const StatCard = ({ label, value, sub, icon: Icon, accent }) => {
  const accents = {
    sky: "from-sky-500/20 to-transparent border-sky-500/20 text-sky-600",
    amber: "from-amber-500/20 to-transparent border-amber-500/20 text-amber-600",
    emerald: "from-emerald-500/20 to-transparent border-emerald-500/20 text-emerald-600",
  };
  return (
    <div className={`card-surface p-6 bg-gradient-to-br ${accents[accent]}`}>
      <div className="flex items-start justify-between mb-3">
        <span className="label-eyebrow">{label}</span>
        <Icon className="w-4 h-4" strokeWidth={1.7} />
      </div>
      <div className="font-display text-4xl font-semibold text-slate-900">{value}</div>
      <div className="text-xs text-slate-500 mt-1">{sub}</div>
    </div>
  );
};

const SkillSection = ({ title, items, icon: Icon, accent, emptyText }) => {
  const colors = {
    sky: "text-sky-600",
    amber: "text-amber-600",
    emerald: "text-emerald-600",
  };
  return (
    <div className="card-surface p-6">
      <div className="flex items-center gap-2 mb-4">
        <Icon className={`w-4 h-4 ${colors[accent]}`} />
        <div className="label-eyebrow">{title}</div>
        <span className="ml-auto text-xs font-mono text-slate-500">{items?.length || 0}</span>
      </div>
      {items?.length === 0 && <div className="text-xs text-slate-500">{emptyText}</div>}
      <div className="space-y-3">
        {items?.map((s) => (
          <SkillBar key={s.skill_id} name={s.skill_name} current={s.current_level} target={s.target_level} evidence={s.evidence} />
        ))}
      </div>
    </div>
  );
};

const PathRow = ({ path }) => (
  <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-slate-200 hover:border-slate-300 hover:bg-slate-50/80 transition">
    <div className="min-w-0">
      <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-2">
        {path.role_title}
        {path.is_promotion && <Badge variant="outline" className="border-amber-500/40 text-amber-700 bg-amber-500/10 text-[0.6rem]">Terfi</Badge>}
      </div>
      <div className="text-[0.7rem] text-slate-500">{path.career_band} · {path.time_to_ready_months} ay</div>
    </div>
    <div className="flex items-center gap-2">
      <div className="text-right">
        <div className="font-mono text-sm font-semibold text-sky-600">%{path.match_score}</div>
        <div className="text-[0.6rem] text-slate-500 uppercase tracking-wider">uyum</div>
      </div>
    </div>
  </div>
);

const CareerPathCard = ({ path, index }) => (
  <div className="card-surface p-6 fade-up" style={{ animationDelay: `${index * 60}ms` }} data-testid={`career-path-${path.role_id}`}>
    <div className="flex items-start justify-between mb-4">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Briefcase className="w-4 h-4 text-sky-600" />
          <h3 className="font-display text-xl font-semibold">{path.role_title}</h3>
          {path.is_promotion && <Badge variant="outline" className="border-amber-500/40 text-amber-700 bg-amber-500/10">Terfi</Badge>}
        </div>
        <div className="text-xs text-slate-500">{path.career_band} · {path.department}</div>
      </div>
      <div className="text-right">
        <div className="font-mono text-2xl font-semibold text-sky-600">%{path.match_score}</div>
        <div className="text-[0.65rem] text-slate-500 uppercase tracking-wider">eşleşme</div>
      </div>
    </div>
    <div className="grid grid-cols-3 gap-3 mb-4">
      <Mini label="Hazır olma" value={`%${path.readiness_score}`} />
      <Mini label="Süre" value={`${path.time_to_ready_months} ay`} icon={Clock} />
      <Mini label="Eksik" value={`${path.key_gaps?.length || 0} beceri`} />
    </div>
    {path.transferable_skills?.length > 0 && (
      <div className="mb-3">
        <div className="label-eyebrow mb-2">Aktarılabilir beceriler</div>
        <div className="flex flex-wrap gap-1.5">
          {path.transferable_skills.map((s) => (
            <span key={s} className="evidence-chip">{s}</span>
          ))}
        </div>
      </div>
    )}
    {path.key_gaps?.length > 0 && (
      <div>
        <div className="label-eyebrow mb-2 text-amber-600">Geliştirilecek</div>
        <div className="flex flex-wrap gap-1.5">
          {path.key_gaps.map((s) => (
            <span key={s} className="px-2.5 py-0.5 rounded-full text-xs border border-amber-500/30 bg-amber-500/10 text-amber-700">{s}</span>
          ))}
        </div>
      </div>
    )}
  </div>
);

const Mini = ({ label, value, icon: Icon }) => (
  <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
    <div className="text-[0.65rem] text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1">
      {Icon && <Icon className="w-3 h-3" />} {label}
    </div>
    <div className="font-mono text-sm font-semibold text-slate-900">{value}</div>
  </div>
);

const MentorRowMini = ({ m, onOpenDetail }) => (
  <button
    type="button"
    onClick={onOpenDetail}
    data-testid={`mentor-mini-${m.mentor_id}`}
    className="w-full flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-sky-300 hover:bg-sky-50/50 transition text-left"
  >
    <Avatar className="w-10 h-10">
      <AvatarImage src={m.avatar} />
      <AvatarFallback>{m.name?.charAt(0)}</AvatarFallback>
    </Avatar>
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium truncate">{m.name}</div>
      <div className="text-[0.7rem] text-slate-500 truncate">{m.title}</div>
    </div>
    <div className="font-mono text-sm font-semibold text-sky-600">%{m.match_score}</div>
  </button>
);

const MentorCard = ({ m, onOpenDetail }) => (
  <div className="card-surface p-6" data-testid={`mentor-card-${m.mentor_id}`}>
    <div className="flex items-start gap-4 mb-5">
      <Avatar className="w-14 h-14 ring-2 ring-sky-500/30">
        <AvatarImage src={m.avatar} />
        <AvatarFallback>{m.name?.charAt(0)}</AvatarFallback>
      </Avatar>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="font-display text-xl font-semibold">{m.name}</h3>
          {m.availability && <Badge className="bg-emerald-500/15 text-emerald-700 border border-emerald-500/30 text-[0.65rem]">Müsait</Badge>}
        </div>
        <div className="text-xs text-slate-500">{m.title} · {m.department}</div>
      </div>
      <div className="text-right">
        <div className="font-mono text-2xl font-semibold text-sky-600">%{m.match_score}</div>
        <div className="text-[0.65rem] text-slate-500 uppercase tracking-wider">uyum</div>
      </div>
    </div>

    {m.expertise_tags?.length > 0 && (
      <div className="mb-4">
        <div className="label-eyebrow mb-2">Uzmanlık</div>
        <div className="flex flex-wrap gap-1.5">
          {m.expertise_tags.map((t) => (
            <span key={t} className="evidence-chip">{t}</span>
          ))}
        </div>
      </div>
    )}

    {m.complementary_skills?.length > 0 && (
      <div className="mb-4">
        <div className="label-eyebrow mb-2 text-amber-600">Sana katacağı beceriler</div>
        <div className="flex flex-wrap gap-1.5">
          {m.complementary_skills.map((t) => (
            <span key={t} className="px-2.5 py-0.5 rounded-full text-xs border border-amber-500/30 bg-amber-500/10 text-amber-700">{t}</span>
          ))}
        </div>
      </div>
    )}

    <div className="grid grid-cols-2 gap-3 mb-4">
      {Object.entries(m.criteria_breakdown || {}).map(([k, v]) => (
        <div key={k} className="rounded-lg border border-slate-200 p-2.5">
          <div className="text-[0.65rem] text-slate-500 uppercase tracking-wider mb-1">{labelize(k)}</div>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-1.5 bg-slate-200/60 rounded-full overflow-hidden">
              <div className="h-full bg-sky-500 rounded-full" style={{ width: `${v}%` }} />
            </div>
            <span className="font-mono text-xs text-slate-700">%{v}</span>
          </div>
        </div>
      ))}
    </div>

    <div className="flex gap-2">
      <Button
        data-testid={`mentor-detail-${m.mentor_id}`}
        onClick={onOpenDetail}
        variant="outline"
        className="flex-1 border-sky-300 text-sky-800 hover:bg-sky-50"
      >
        <Sparkles className="w-4 h-4 mr-2" /> Eşleşme detayını gör
      </Button>
      <Button
        data-testid={`mentor-request-${m.mentor_id}`}
        onClick={onOpenDetail}
        className="flex-1 bg-sky-600 hover:bg-sky-700 text-white"
      >
        <GraduationCap className="w-4 h-4 mr-2" /> Talep gönder
      </Button>
    </div>
  </div>
);

const labelize = (k) => ({
  skill_complementarity: "Beceri Tamamlayıcılığı",
  career_alignment: "Kariyer Uyumu",
  availability: "Müsaitlik",
  experience_gap: "Deneyim Farkı",
}[k] || k);

/**
 * YggEmployeeSection — Employee's YGG view:
 * - Aktif döngü survey (varsa) + "Şu an sıra kimde" göstergesi
 * - Multi-year history timeline (sadece kalibre edilmiş dönemler)
 */
function YggEmployeeSection({ empId }) {
  const [activeCycle, setActiveCycle] = useState(null);
  const [myEval, setMyEval] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    setLoading(true);
    listYggCycles().then(async (d) => {
      const active = (d.items || []).find((c) => c.status === "active");
      setActiveCycle(active || null);
      if (active) {
        try {
          const { getYggEvaluation } = await import("../lib/api");
          const ev = await getYggEvaluation(active.id, empId);
          setMyEval(ev);
        } catch { setMyEval(null); }
      }
    }).finally(() => setLoading(false));
  }, [empId, refreshKey]);

  const currentStage = (() => {
    if (!myEval) return null;
    const s = myEval.stage_status || {};
    if (s.calibration === "completed") return { key: "closed", label: "Tamamlandı", who: "İK kalibre etti", color: "emerald" };
    if (s.director_review === "completed") return { key: "calibration", label: "Kalibrasyon aşamasında", who: "İK", color: "amber" };
    if (s.manager_eval === "completed") return { key: "director_review", label: "Direktör onayında", who: "Direktör", color: "indigo" };
    if (s.survey === "completed") return { key: "manager_eval", label: "Yönetici değerlendiriyor", who: "Yöneticin", color: "sky" };
    return { key: "survey", label: "Senin sıran — anketi doldur", who: "Sen", color: "rose" };
  })();

  return (
    <div className="space-y-6">
      {loading ? (
        <div className="card-surface p-6 text-sm text-slate-400">Yükleniyor…</div>
      ) : activeCycle ? (
        <>
          {currentStage && (
            <div className={`rounded-xl border p-4 flex items-center gap-3 ${currentStage.who === "Sen" ? "border-rose-200 bg-rose-50" : "border-slate-200 bg-slate-50"}`} data-testid="ygg-current-stage-banner">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${currentStage.who === "Sen" ? "bg-rose-500" : "bg-slate-400"}`}>
                {currentStage.who === "Sen" ? "!" : "⋯"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">{activeCycle.name}</div>
                <div className="text-sm font-semibold text-slate-800">{currentStage.label}</div>
                <div className="text-xs text-slate-600">{currentStage.who === "Sen" ? "Aşağıdaki anketi doldurunca sıra yöneticine geçecek." : `Şu an bekleyen: ${currentStage.who}. Sonra sırası ile Direktör → İK.`}</div>
              </div>
              {/* Mini 4-step visual */}
              <div className="hidden md:flex items-center gap-1">
                {["survey","manager_eval","director_review","calibration"].map((k, i) => {
                  const done = (myEval?.stage_status || {})[k] === "completed";
                  const isCurrent = currentStage.key === k;
                  return (
                    <div key={k} className="flex items-center">
                      <div className={`w-6 h-6 rounded-full text-[10px] font-bold flex items-center justify-center ${done ? "bg-emerald-500 text-white" : isCurrent ? "bg-sky-500 text-white ring-2 ring-sky-200" : "bg-slate-200 text-slate-500"}`}>
                        {done ? "✓" : i + 1}
                      </div>
                      {i < 3 && <div className={`w-4 h-0.5 ${done ? "bg-emerald-400" : "bg-slate-200"}`} />}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <YggSurveyForm cycleId={activeCycle.id} employeeId={empId} cycleName={activeCycle.name} onSaved={() => setRefreshKey((k) => k + 1)} />
        </>
      ) : (
        <div className="card-surface p-6" data-testid="ygg-no-active">
          <div className="label-eyebrow mb-1">Aktif YGG Yok</div>
          <p className="text-sm text-slate-600">Şu an açık bir Yetenek Gözden Geçirme döngüsü bulunmuyor. İK yeni bir döngü başlattığında burada anketin görünecek.</p>
        </div>
      )}
      <YggHistoryTimeline employeeId={empId} />
    </div>
  );
}

